'use client';

import { createClient } from '@/lib/supabase/client';

export type CannabisAmount = 'none' | '1_2_puffs' | '3_5_puffs' | 'moderate_session' | 'heavy_session';
export type CannabisTime = 'morning' | 'afternoon' | 'evening' | 'before_bed';
export type AlcoholLevel = 'none' | 'light' | 'moderate' | 'heavy';

export interface BehaviorLog {
  id: string;
  userId: string;
  logDate: string;
  cannabisAmount: CannabisAmount;
  cannabisTime?: CannabisTime;
  cannabisNotes?: string;
  alcoholLevel: AlcoholLevel;
  hadEnergyDrink: boolean;
  energyDrinkCount: number;
  coffeeCups: number;
  hadLateNightEating: boolean;
  hadTakeaway: boolean;
  hadSocialEvent: boolean;
  wasHighStress: boolean;
  hadPoorSleepPrevious: boolean;
  caloriesBeforeCannabis: number;
  caloriesAfterCannabis: number;
  proteinBeforeCannabis: number;
  proteinAfterCannabis: number;
  unplannedCaloriesAfter: number;
  sleepDurationHours?: number;
  sleepQualityRating?: number;
  morningEnergyRating?: number;
  trainingCompleted: boolean;
  trainingIntensity?: number;
  totalCalories: number;
  totalProteinG: number;
  totalSteps: number;
  forgeScore: number;
  weightKg?: number;
  targetCalories: number;
  targetProteinG: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BehaviorCorrelation {
  behavior: string;
  behaviorLabel: string;
  daysWithBehavior: number;
  daysWithout: number;
  withCalories: number;
  withoutCalories: number;
  withProtein: number;
  withoutProtein: number;
  withSleep: number;
  withoutSleep: number;
  withSteps: number;
  withoutSteps: number;
  withForgeScore: number;
  withoutForgeScore: number;
  withWeightChange: number;
  withoutWeightChange: number;
  caloriesDiff: number;
  proteinDiff: number;
  sleepDiff: number;
  stepsDiff: number;
  forgeScoreDiff: number;
  weightChangeDiff: number;
}

export interface AfterHoursAnalysis {
  avgCaloriesAfterCannabis: number;
  avgCaloriesWithoutCannabis: number;
  lateNightCalorieIncrease: number;
  avgProteinAfterCannabis: number;
  avgProteinWithoutCannabis: number;
  avgUnplannedCalories: number;
  cannabisDaysCount: number;
  nonCannabisDaysCount: number;
}

export interface WeeklyInsight {
  hasEnoughData: boolean;
  trackedDays: number;
  insights: string[];
  cannabisNeutralToWeightLoss: boolean;
  cannabisAssociatedWithExtraCalories: boolean;
  cannabisAssociatedWithBetterSleep: boolean;
  cannabisAssociatedWithLowerProtein: boolean;
}

function mapBehaviorLog(row: Record<string, unknown>): BehaviorLog {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    logDate: row.log_date as string,
    cannabisAmount: (row.cannabis_amount as CannabisAmount) || 'none',
    cannabisTime: row.cannabis_time as CannabisTime | undefined,
    cannabisNotes: row.cannabis_notes as string | undefined,
    alcoholLevel: (row.alcohol_level as AlcoholLevel) || 'none',
    hadEnergyDrink: Boolean(row.had_energy_drink),
    energyDrinkCount: Number(row.energy_drink_count) || 0,
    coffeeCups: Number(row.coffee_cups) || 0,
    hadLateNightEating: Boolean(row.had_late_night_eating),
    hadTakeaway: Boolean(row.had_takeaway),
    hadSocialEvent: Boolean(row.had_social_event),
    wasHighStress: Boolean(row.was_high_stress),
    hadPoorSleepPrevious: Boolean(row.had_poor_sleep_previous),
    caloriesBeforeCannabis: Number(row.calories_before_cannabis) || 0,
    caloriesAfterCannabis: Number(row.calories_after_cannabis) || 0,
    proteinBeforeCannabis: Number(row.protein_before_cannabis) || 0,
    proteinAfterCannabis: Number(row.protein_after_cannabis) || 0,
    unplannedCaloriesAfter: Number(row.unplanned_calories_after) || 0,
    sleepDurationHours: row.sleep_duration_hours != null ? Number(row.sleep_duration_hours) : undefined,
    sleepQualityRating: row.sleep_quality_rating != null ? Number(row.sleep_quality_rating) : undefined,
    morningEnergyRating: row.morning_energy_rating != null ? Number(row.morning_energy_rating) : undefined,
    trainingCompleted: Boolean(row.training_completed),
    trainingIntensity: row.training_intensity != null ? Number(row.training_intensity) : undefined,
    totalCalories: Number(row.total_calories) || 0,
    totalProteinG: Number(row.total_protein_g) || 0,
    totalSteps: Number(row.total_steps) || 0,
    forgeScore: Number(row.forge_score) || 0,
    weightKg: row.weight_kg != null ? Number(row.weight_kg) : undefined,
    targetCalories: Number(row.target_calories) || 2000,
    targetProteinG: Number(row.target_protein_g) || 150,
    notes: row.notes as string | undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function avg(arr: number[]): number {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

export const forgeBehaviorService = {
  async saveBehaviorLog(data: Partial<BehaviorLog> & { logDate: string }): Promise<BehaviorLog | null> {
    const supabase = createClient();
    if (!supabase) return null;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const payload = {
      user_id: user.id,
      log_date: data.logDate,
      cannabis_amount: data.cannabisAmount || 'none',
      cannabis_time: data.cannabisTime || null,
      cannabis_notes: data.cannabisNotes || null,
      alcohol_level: data.alcoholLevel || 'none',
      had_energy_drink: data.hadEnergyDrink || false,
      energy_drink_count: data.energyDrinkCount || 0,
      coffee_cups: data.coffeeCups || 0,
      had_late_night_eating: data.hadLateNightEating || false,
      had_takeaway: data.hadTakeaway || false,
      had_social_event: data.hadSocialEvent || false,
      was_high_stress: data.wasHighStress || false,
      had_poor_sleep_previous: data.hadPoorSleepPrevious || false,
      calories_before_cannabis: data.caloriesBeforeCannabis || 0,
      calories_after_cannabis: data.caloriesAfterCannabis || 0,
      protein_before_cannabis: data.proteinBeforeCannabis || 0,
      protein_after_cannabis: data.proteinAfterCannabis || 0,
      unplanned_calories_after: data.unplannedCaloriesAfter || 0,
      sleep_duration_hours: data.sleepDurationHours ?? null,
      sleep_quality_rating: data.sleepQualityRating ?? null,
      morning_energy_rating: data.morningEnergyRating ?? null,
      training_completed: data.trainingCompleted || false,
      training_intensity: data.trainingIntensity ?? null,
      total_calories: data.totalCalories || 0,
      total_protein_g: data.totalProteinG || 0,
      total_steps: data.totalSteps || 0,
      forge_score: data.forgeScore || 0,
      weight_kg: data.weightKg ?? null,
      target_calories: data.targetCalories || 2000,
      target_protein_g: data.targetProteinG || 150,
      notes: data.notes || null,
    };

    const { data: result, error } = await supabase
      .from('forge_behavior_logs')
      .upsert(payload, { onConflict: 'user_id,log_date' })
      .select()
      .single();

    if (error) {
      console.error('saveBehaviorLog error:', error.message);
      return null;
    }
    return mapBehaviorLog(result);
  },

  async getTodayLog(): Promise<BehaviorLog | null> {
    const supabase = createClient();
    if (!supabase) return null;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('forge_behavior_logs')
      .select('*')
      .eq('user_id', user.id)
      .eq('log_date', today)
      .single();

    if (error || !data) return null;
    return mapBehaviorLog(data);
  },

  async getLogsForRange(startDate: string, endDate: string): Promise<BehaviorLog[]> {
    const supabase = createClient();
    if (!supabase) return [];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('forge_behavior_logs')
      .select('*')
      .eq('user_id', user.id)
      .gte('log_date', startDate)
      .lte('log_date', endDate)
      .order('log_date', { ascending: false });

    if (error) return [];
    return (data || []).map(mapBehaviorLog);
  },

  async getAllLogs(): Promise<BehaviorLog[]> {
    const supabase = createClient();
    if (!supabase) return [];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('forge_behavior_logs')
      .select('*')
      .eq('user_id', user.id)
      .order('log_date', { ascending: false });

    if (error) return [];
    return (data || []).map(mapBehaviorLog);
  },

  computeCorrelation(logs: BehaviorLog[], behaviorKey: keyof BehaviorLog, behaviorLabel: string): BehaviorCorrelation | null {
    const withBehavior = logs.filter(l => {
      const val = l[behaviorKey];
      if (typeof val === 'boolean') return val === true;
      if (typeof val === 'string') return val !== 'none';
      return false;
    });
    const without = logs.filter(l => {
      const val = l[behaviorKey];
      if (typeof val === 'boolean') return val === false;
      if (typeof val === 'string') return val === 'none';
      return false;
    });

    if (withBehavior.length < 2 || without.length < 2) return null;

    const withCalories = avg(withBehavior.map(l => l.totalCalories));
    const withoutCalories = avg(without.map(l => l.totalCalories));
    const withProtein = avg(withBehavior.map(l => l.totalProteinG));
    const withoutProtein = avg(without.map(l => l.totalProteinG));
    const withSleep = avg(withBehavior.filter(l => l.sleepDurationHours != null).map(l => l.sleepDurationHours!));
    const withoutSleep = avg(without.filter(l => l.sleepDurationHours != null).map(l => l.sleepDurationHours!));
    const withSteps = avg(withBehavior.map(l => l.totalSteps));
    const withoutSteps = avg(without.map(l => l.totalSteps));
    const withForgeScore = avg(withBehavior.map(l => l.forgeScore));
    const withoutForgeScore = avg(without.map(l => l.forgeScore));

    // Weight change: compare sequential weight entries
    const withWeightChange = 0;
    const withoutWeightChange = 0;

    return {
      behavior: behaviorKey as string,
      behaviorLabel,
      daysWithBehavior: withBehavior.length,
      daysWithout: without.length,
      withCalories,
      withoutCalories,
      withProtein,
      withoutProtein,
      withSleep,
      withoutSleep,
      withSteps,
      withoutSteps,
      withForgeScore,
      withoutForgeScore,
      withWeightChange,
      withoutWeightChange,
      caloriesDiff: withCalories - withoutCalories,
      proteinDiff: withProtein - withoutProtein,
      sleepDiff: withSleep - withoutSleep,
      stepsDiff: withSteps - withoutSteps,
      forgeScoreDiff: withForgeScore - withoutForgeScore,
      weightChangeDiff: withWeightChange - withoutWeightChange,
    };
  },

  computeAfterHoursAnalysis(logs: BehaviorLog[]): AfterHoursAnalysis {
    const cannabisDays = logs.filter(l => l.cannabisAmount !== 'none');
    const nonCannabisDays = logs.filter(l => l.cannabisAmount === 'none');

    const avgCaloriesAfterCannabis = avg(cannabisDays.map(l => l.caloriesAfterCannabis));
    const avgCaloriesWithoutCannabis = avg(nonCannabisDays.map(l => l.totalCalories));
    const avgProteinAfterCannabis = avg(cannabisDays.map(l => l.proteinAfterCannabis));
    const avgProteinWithoutCannabis = avg(nonCannabisDays.map(l => l.totalProteinG));
    const avgUnplannedCalories = avg(cannabisDays.map(l => l.unplannedCaloriesAfter));

    return {
      avgCaloriesAfterCannabis,
      avgCaloriesWithoutCannabis,
      lateNightCalorieIncrease: avgCaloriesAfterCannabis - avgCaloriesWithoutCannabis,
      avgProteinAfterCannabis,
      avgProteinWithoutCannabis,
      avgUnplannedCalories,
      cannabisDaysCount: cannabisDays.length,
      nonCannabisDaysCount: nonCannabisDays.length,
    };
  },

  computeWeeklyInsights(logs: BehaviorLog[]): WeeklyInsight {
    const MIN_DAYS = 10;
    if (logs.length < MIN_DAYS) {
      return {
        hasEnoughData: false,
        trackedDays: logs.length,
        insights: [],
        cannabisNeutralToWeightLoss: false,
        cannabisAssociatedWithExtraCalories: false,
        cannabisAssociatedWithBetterSleep: false,
        cannabisAssociatedWithLowerProtein: false,
      };
    }

    const cannabisDays = logs.filter(l => l.cannabisAmount !== 'none');
    const nonCannabisDays = logs.filter(l => l.cannabisAmount === 'none');
    const insights: string[] = [];

    const calDiff = avg(cannabisDays.map(l => l.totalCalories)) - avg(nonCannabisDays.map(l => l.totalCalories));
    const proteinDiff = avg(cannabisDays.map(l => l.totalProteinG)) - avg(nonCannabisDays.map(l => l.totalProteinG));
    const sleepDiff = avg(cannabisDays.filter(l => l.sleepDurationHours != null).map(l => l.sleepDurationHours!))
      - avg(nonCannabisDays.filter(l => l.sleepDurationHours != null).map(l => l.sleepDurationHours!));
    const scoreDiff = avg(cannabisDays.map(l => l.forgeScore)) - avg(nonCannabisDays.map(l => l.forgeScore));

    const cannabisAssociatedWithExtraCalories = calDiff > 100;
    const cannabisAssociatedWithBetterSleep = sleepDiff > 0.2;
    const cannabisAssociatedWithLowerProtein = proteinDiff < -5;
    const cannabisNeutralToWeightLoss = Math.abs(calDiff) < 100 && Math.abs(scoreDiff) < 3;

    if (cannabisNeutralToWeightLoss && cannabisDays.length >= 3) {
      insights.push('Cannabis use had no measurable effect on weight loss this week.');
    }
    if (cannabisAssociatedWithExtraCalories && cannabisDays.length >= 3) {
      insights.push(`Cannabis days were associated with ${Math.round(calDiff)} additional calories.`);
    }
    if (cannabisAssociatedWithBetterSleep && cannabisDays.length >= 3) {
      insights.push(`Cannabis days were associated with ${(sleepDiff * 60).toFixed(0)} extra minutes of sleep.`);
    }
    if (cannabisAssociatedWithLowerProtein && cannabisDays.length >= 3) {
      insights.push(`Cannabis days showed ${Math.abs(Math.round(proteinDiff))}g lower average protein intake.`);
    }
    if (Math.abs(scoreDiff) < 2 && cannabisDays.length >= 3) {
      insights.push('Cannabis days showed no significant impact on Forge Score.');
    }

    // Stress insights
    const stressDays = logs.filter(l => l.wasHighStress);
    if (stressDays.length >= 3) {
      const stressCalDiff = avg(stressDays.map(l => l.totalCalories)) - avg(logs.filter(l => !l.wasHighStress).map(l => l.totalCalories));
      if (stressCalDiff > 150) {
        insights.push(`High stress days were associated with ${Math.round(stressCalDiff)} additional calories.`);
      }
    }

    // Late night eating
    const lneDays = logs.filter(l => l.hadLateNightEating);
    if (lneDays.length >= 3) {
      insights.push(`Late night eating was logged on ${lneDays.length} days this period.`);
    }

    return {
      hasEnoughData: true,
      trackedDays: logs.length,
      insights,
      cannabisNeutralToWeightLoss,
      cannabisAssociatedWithExtraCalories,
      cannabisAssociatedWithBetterSleep,
      cannabisAssociatedWithLowerProtein,
    };
  },

  getBehaviorFrequency(logs: BehaviorLog[]) {
    const total = logs.length || 1;
    return {
      cannabis: logs.filter(l => l.cannabisAmount !== 'none').length,
      cannabisPct: Math.round((logs.filter(l => l.cannabisAmount !== 'none').length / total) * 100),
      alcohol: logs.filter(l => l.alcoholLevel !== 'none').length,
      alcoholPct: Math.round((logs.filter(l => l.alcoholLevel !== 'none').length / total) * 100),
      lateNightEating: logs.filter(l => l.hadLateNightEating).length,
      lateNightEatingPct: Math.round((logs.filter(l => l.hadLateNightEating).length / total) * 100),
      takeaway: logs.filter(l => l.hadTakeaway).length,
      takeawayPct: Math.round((logs.filter(l => l.hadTakeaway).length / total) * 100),
      stress: logs.filter(l => l.wasHighStress).length,
      stressPct: Math.round((logs.filter(l => l.wasHighStress).length / total) * 100),
      socialEvents: logs.filter(l => l.hadSocialEvent).length,
      socialEventsPct: Math.round((logs.filter(l => l.hadSocialEvent).length / total) * 100),
      energyDrinks: logs.filter(l => l.hadEnergyDrink).length,
      energyDrinksPct: Math.round((logs.filter(l => l.hadEnergyDrink).length / total) * 100),
      total,
    };
  },
};
